import { useEffect, type ReactNode } from "react";
import { Modal, Drawer, Input as AntInput } from "antd";
import type { InputProps } from "antd/es/input";
import type { TextAreaProps } from "antd/es/input/TextArea";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";

// Dialog/DialogTrigger/DialogContent stay as no-ops — unused compound-API
// scaffolding carried over from the old shadcn-style naming. Kept only so
// any lingering imports elsewhere don't break; safe to delete once confirmed unused.
export function Dialog({ children }: { open?: boolean; onOpenChange?: (open: boolean) => void; children: React.ReactNode }) {
  return <>{children}</>;
}
export function DialogContent() { return null; }
export function DialogHeader({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={cn(className)}>{children}</div>;
}
export function DialogTitle({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={cn("text-base font-semibold", className)}>{children}</div>;
}
export function DialogDescription({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <p className={cn("text-sm text-[#75787B]", className)}>{children}</p>;
}
export function DialogFooter({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={cn("mt-4 flex justify-end gap-2", className)}>{children}</div>;
}
export function DialogTrigger({ children }: { children: React.ReactNode; asChild?: boolean }) {
  return <>{children}</>;
}

interface ModalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** A plain number is used as a px value; a CSS length/expression string
   *  (e.g. "min(85vw, 1400px)") is passed through as-is — useful for a
   *  modal that should scale with the viewport instead of sitting at one
   *  fixed pixel width on every screen size. */
  width?: number | string;
  hideCloseButton?: boolean;
  /** The cross shows but can't be pressed, and Escape / clicking outside don't close it either (e.g. while saving). */
  closeDisabled?: boolean;
  mask?: boolean;
  /** Vertically centers the modal instead of AntD's default fixed top
   *  offset, so a tall-content modal (whose height varies a lot by screen)
   *  doesn't get pushed above the fold on a short viewport. AntD's own
   *  `.ant-modal-wrap` already scrolls the whole modal
   *  (title, close button and body together, as ONE scroll context) when
   *  content is taller than the viewport — deliberately not layering a
   *  second, inner scrollable region on top of that here, since two nested
   *  scrollbars fighting for the same corner is what made the close button
   *  overlap the scrollbar in the first place. Opt-in (default false,
   *  AntD's original behavior) so existing modals are unaffected. */
  centered?: boolean;
}

export function AppModal({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  className,
  width = 520,
  hideCloseButton = false,
  closeDisabled = false,
  mask = true,
  centered = false,
}: ModalDialogProps) {
  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={footer ?? null}
      width={width}
      centered={centered}
      // Caps the modal to the viewport on every screen size instead of a
      // fixed pixel width/height that overflows a small screen or leaves a
      // huge gap on a large one — width/height above are the INTENDED size,
      // this is just the ceiling.
      style={{ maxWidth: "calc(100vw - 32px)" }}
      closable={hideCloseButton ? false : closeDisabled ? { disabled: true } : true}
      keyboard={!closeDisabled}
      mask={mask}
      closeIcon={<Icon icon="lucide:x" className="h-4 w-4 text-black/45 hover:text-[#3A3D42]" />}
      className={cn("[&_.ant-modal-content]:rounded-lg [&_.ant-modal-content]:p-6", className)}
      maskClosable={!closeDisabled}
      // Escape-to-close, body-scroll-lock, and focus-trap are all AntD Modal
      // built-ins — previously ~15 lines of manual useEffect wiring per modal.
    >
      {(title || description) && (
        <div className="mb-4 pr-6">
          {title && <h2 className="text-base font-semibold text-[#3A3D42]">{title}</h2>}
          {description && <p className="mt-1 text-sm text-[#75787B]">{description}</p>}
        </div>
      )}
      {children}
    </Modal>
  );
}

interface SlideOverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children?: ReactNode;
  width?: number;
}

export function SlideOver({ open, onOpenChange, children, width = 480 }: SlideOverProps) {
  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      placement="right"
      width={width}
      closable
      closeIcon={<Icon icon="lucide:x" className="h-4 w-4 text-black/45 hover:text-[#111827]" />}
      className="[&_.ant-drawer-content]:shadow-[-18px_0_40px_rgba(17,24,39,0.18)]"
      styles={{ body: { padding: 0 } }}
      // Drawer's slide-in/out transition + backdrop fade replace the old
      // instant show/hide — this is the single biggest "smoothness" win.
    >
      {children}
    </Drawer>
  );
}

export function Input({ className, ...props }: InputProps) {
  const isBorderless = props.variant === "borderless";
  return (
    <AntInput
      className={cn(
        "!h-9 !rounded-md !text-sm !text-[#3A3D42]",
        isBorderless
          ? "msq-input-borderless !border-0 !shadow-none focus:!border-0 focus:!shadow-none"
          : "!border-[#DAD9D8] focus:!border-[#E4002B]/50 focus:!shadow-none",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: TextAreaProps) {
  const isBorderless = props.variant === "borderless";
  return (
    <AntInput.TextArea
      className={cn(
        "!rounded-md !text-sm !text-[#3A3D42]",
        isBorderless
          ? "msq-input-borderless !border-0 !shadow-none focus:!border-0 focus:!shadow-none"
          : "!border-[#DAD9D8] focus:!border-[#E4002B]/50 focus:!shadow-none",
        className,
      )}
      {...props}
    />
  );
}
